import { Editor, Range, Transforms } from 'slate'
import { isEqual } from 'lodash-es'

import { formatHref } from '../../utils/link-sanitizer'

export function withLinks(editor) {
  const { isInline, normalizeNode } = editor

  editor.isInline = (element) => isLink(element) || isInline(element)

  editor.normalizeNode = (entry) => {
    const [node, path] = entry

    // Removing links without any text.
    if (isLink(node) && node.children.every((child) => child.text === '')) {
      const isSelected = editor.selection && isEqual(editor.selection.focus.path, [...path, 0])
      // When the link that is going to be removed is selected, the selection needs to be adapted afterwards.
      if (isSelected) {
        const newFocus = { ...editor.selection.focus, path: editor.selection.focus.path.slice(0, -1) }
        Transforms.removeNodes(editor, { at: path })
        Transforms.select(editor, { anchor: newFocus, focus: newFocus })
      } else {
        Transforms.removeNodes(editor, { at: path })
      }
      return
    }

    normalizeNode(entry)
  }

  return editor
}

function isLink(node) {
  return node.type === 'link'
}

export function getLink(editor) {
  const [match] = Editor.nodes(editor, { match: isLink })
  return match
}

export function isLinkFocused(editor) {
  if (!editor.selection) return false
  const [, focusedNodePath] = Editor.node(editor, Range.start(editor.selection))
  const [parent] = Editor.parent(editor, focusedNodePath)

  return isLink(parent)
}

export function isAnyLinkSelected(editor) {
  const nodes = Array.from(Editor.nodes(editor, { match: isLink }))

  return nodes.length > 0
}

export function unlink(editor) {
  Transforms.unwrapNodes(editor, { match: isLink })
}

export function insertLink(editor, href, text, isExternal) {
  const url = formatHref(href)
  const isCollapsed = editor.selection && Range.isCollapsed(editor.selection)

  const link = {
    type: 'link',
    url,
    children: isCollapsed ? [{ text: text || href }] : undefined,
    isExternal: isExternal !== undefined ? isExternal : isExternalURL(url),
  }

  if (isCollapsed) Transforms.insertNodes(editor, link)
  else Transforms.wrapNodes(editor, link, { split: true })
}

// https://gist.github.com/dperini/729294
// https://mathiasbynens.be/demo/url-regex
export function isValidHref(href) {
  let sanitizedHref = formatHref(href)

  if (sanitizedHref.startsWith('/') || sanitizedHref.startsWith('#')) sanitizedHref = 'http://epages.de' + sanitizedHref

  const mailPattern = /^mailto:([^@]+)@([^@]+)$/
  const telPattern = /^tel:([^@]+)$/
  const hrefPattern = new RegExp(
    '^' +
      // protocol identifier (optional)
      // short syntax // still required
      '(?:(?:(?:https?|ftp):)?\\/\\/)' +
      // user:pass BasicAuth (optional)
      '(?:\\S+(?::\\S*)?@)?' +
      '(?:' +
      // IP address exclusion
      // private & local networks
      '(?!(?:10|127)(?:\\.\\d{1,3}){3})' +
      '(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})' +
      '(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})' +
      // IP address dotted notation octets
      // excludes loopback network 0.0.0.0
      // excludes reserved space >= 224.0.0.0
      // excludes network & broadcast addresses
      // (first & last IP address of each class)
      '(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
      '(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
      '(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))' +
      '|' +
      // host & domain names, may end with dot
      // can be replaced by a shortest alternative
      // (?![-_])(?:[-\\w\\u00a1-\\uffff]{0,63}[^-_]\\.)+
      '(?:' +
      '(?:' +
      '[a-z0-9\\u00a1-\\uffff]' +
      '[a-z0-9\\u00a1-\\uffff_-]{0,62}' +
      ')?' +
      '[a-z0-9\\u00a1-\\uffff]\\.' +
      ')+' +
      // TLD identifier name, may end with dot
      '(?:[a-z\\u00a1-\\uffff]{2,}\\.?)' +
      ')' +
      // port number (optional)
      '(?::\\d{2,5})?' +
      // resource path (optional)
      '(?:[/?#]\\S*)?' +
      '$',
    'i',
  )

  if (hrefType(sanitizedHref) === 'email') return mailPattern.test(sanitizedHref)
  if (hrefType(sanitizedHref) === 'tel') return telPattern.test(sanitizedHref)
  if (hrefType(sanitizedHref) === 'javascript') return true

  return hrefPattern.test(sanitizedHref)
}

function hrefType(href) {
  if (href.startsWith('mailto:')) return 'email'
  if (href.startsWith('tel:')) return 'tel'
  if (href.startsWith('javascript:')) return 'javascript'
  return 'link'
}

export function isExternalURL(href) {
  return hrefType(href) === 'link' && location.host !== new URL(href, location.origin).host
}
